import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    useTheme,
    styled,
    Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PendingIcon from '@mui/icons-material/Pending';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { formatDate } from '../../utils/functions';

const HoverPaper = styled(Paper)(({ theme }) => ({
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'scale(1.02)',
        boxShadow: theme.shadows[5],
    },
}));

const OrderItem = (props) => {
    const { orderId, name, image, price, quantity, createdAt, deliveredAt, orderStatus, paymentInfo } = props;
    const theme = useTheme();

    // Extract payment status
    const paymentStatus = paymentInfo?.status || 'pending';
    const paymentMessage = paymentInfo?.message;

    return (
        <Link to={`/order_details/${orderId}`} style={{ textDecoration: 'none' }}>
            <HoverPaper
                sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    bgcolor: 'background.paper',
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    {/* Image */}
                    <Grid item xs={12} sm={3}>
                        <Box
                            component="img"
                            src={image}
                            alt={name}
                            sx={{
                                width: '100%',
                                maxWidth: 120,
                                height: 'auto',
                                maxHeight: 80,
                                objectFit: 'contain',
                                borderRadius: 1,
                            }}
                        />
                    </Grid>

                    {/* Details */}
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {name.length > 40 ? `${name.substring(0, 40)}...` : name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Quantity: {quantity} | Total: ₹{(quantity * price).toLocaleString()}
                        </Typography>
                    </Grid>

                    {/* Status & Price */}
                    <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                            ₹{price.toLocaleString()}
                        </Typography>

                        {/* Payment Status Chip */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 1 }}>
                            <Chip
                                icon={<CreditCardIcon />}
                                label={
                                    paymentStatus === 'success'
                                        ? 'Payment Successful'
                                        : paymentStatus === 'pending'
                                            ? 'Payment Pending'
                                            : 'Payment Failed'
                                }
                                size="small"
                                color={
                                    paymentStatus === 'success'
                                        ? 'success'
                                        : paymentStatus === 'pending'
                                            ? 'warning'
                                            : 'error'
                                }
                                variant="outlined"
                            />
                        </Box>

                        {orderStatus === "Shipped" ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <LocalShippingIcon sx={{ color: theme.palette.warning.main, mr: 0.5, fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary">
                                    Shipped
                                </Typography>
                            </Box>
                        ) : orderStatus === "Delivered" ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <CheckCircleIcon sx={{ color: theme.palette.success.main, mr: 0.5, fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary">
                                    Delivered on {formatDate(deliveredAt)}
                                </Typography>
                            </Box>
                        ) : paymentStatus === "success" ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <PendingIcon sx={{ color: theme.palette.info.main, mr: 0.5, fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary">
                                    Ordered on {formatDate(createdAt)}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <PendingIcon sx={{ color: theme.palette.info.main, mr: 0.5, fontSize: 20 }} />
                                <Typography variant="caption" color="text.secondary">
                                    Order pending since {formatDate(createdAt)}
                                </Typography>
                            </Box>
                        )}


                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {orderStatus === "Delivered"
                                ? "Item has been delivered"
                                : orderStatus === "Shipped"
                                    ? "Item has been shipped"
                                    : paymentStatus === 'success'
                                        ? "Order is processed"
                                        : "Order needs attention"}
                        </Typography>
                        {paymentStatus !== 'success' && paymentMessage && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main' }}>
                                {paymentMessage}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </HoverPaper>
        </Link>
    );
};

export default OrderItem;
